import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';
import {isIosDevice} from '@docma-mm-core/utils/utils';
import {getActiveEditable, ListenerGroup, onKeyboardViewportChange} from './shared/keyboard-viewport.util';

const KEYBOARD_MIN_PX = 150;
const KEYBOARD_OPEN_CLASS = 'mm-keyboard-open';

@Injectable({providedIn: 'root'})
export class MmKeyboardStateService {

  private listeners = new ListenerGroup();
  private closedBaseline = {width: 0, height: 0};
  private readonly openSubject = new BehaviorSubject<boolean>(false);

  readonly keyboardOpen$: Observable<boolean> = this.openSubject.pipe(distinctUntilChanged());

  get isKeyboardOpen(): boolean {
    return this.openSubject.value;
  }

  start(): void {
    if (this.listeners.active || typeof window === 'undefined' || !window.visualViewport) {
      return;
    }
    onKeyboardViewportChange(this.listeners, event => this.update(event));
    this.update();
  }

  private update(event?: Event): void {
    const vv = window.visualViewport;
    if (!vv) {
      return;
    }
    const layoutWidth = document.documentElement.clientWidth;
    const layoutHeight = document.documentElement.clientHeight;
    const focused = getActiveEditable();
    const keyboardHeight = Math.round(layoutHeight - vv.height);
    let open = keyboardHeight > KEYBOARD_MIN_PX;
    // Android WebViews resize the layout viewport together with the visual one, so the visual gap
    // above stays ~0 there — the keyboard signal is the layout height dropping below its
    // keyboard-closed baseline instead. A split-screen resize while a field is focused reads as a
    // keyboard too — accepted for now.
    if (!open && !isIosDevice()) {
      open = this.closedBaseline.height - layoutHeight > KEYBOARD_MIN_PX;
    }
    // WebKit can keep reporting a keyboard-sized viewport with no keyboard on screen (stuck inset
    // after overlay-dismissed keyboards; survives reloads) — trust the shrunken viewport only while
    // an editable is focused. Distrust may only fire on touchstart/focusin/boot: focusout and vv
    // blips happen mid focus-hop with the keyboard legitimately open, and deciding there flaps the
    // class within a frame and yanks the scroller.
    const distrustEligible = !event || event.type === 'touchstart' || event.type === 'focusin';
    if (open && distrustEligible && !focused) {
      open = false;
    }
    // baseline may only be captured while the keyboard is provably away, otherwise a focus-hop
    // (focusout fires with the layout still shrunken) poisons it and the class drops mid-keyboard.
    // A width change can't be a keyboard — re-seed immediately so a rotation is never read as one.
    if (layoutWidth !== this.closedBaseline.width || (!open && !focused)) {
      this.closedBaseline = {width: layoutWidth, height: layoutHeight};
    }
    this.setOpen(open);
  }

  private setOpen(open: boolean): void {
    if (this.openSubject.value !== open) {
      this.openSubject.next(open);
    }
    document.documentElement.classList.toggle(KEYBOARD_OPEN_CLASS, open);
  }
}
